import { useEffect, useId, useState } from 'react';
import { IoCloseOutline } from 'react-icons/io5';
import { useDebouncedValue, useLatestRequest } from './useAdminTable';
import './EntityPicker.css';

/**
 * Search-and-pick for one record (a reader, a family, a user), in place of a
 * raw UUID box. `search(query)` resolves to [{ id, label, sub? }].
 *
 * Controlled by `value` (the id) and `valueLabel`; `onChange(id, label)` is
 * called with ('', '') when cleared.
 */
const EntityPicker = ({ value, valueLabel = '', onChange, search, placeholder = 'Search…', ariaLabel }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const debouncedQuery = useDebouncedValue(query.trim());
  const beginRequest = useLatestRequest();
  const listId = useId();

  useEffect(() => {
    if (!debouncedQuery) {
      setResults([]);
      return;
    }
    const request = beginRequest();
    search(debouncedQuery)
      .then((found) => {
        if (!request.isCurrent()) return;
        setResults(found);
        setError(null);
      })
      .catch(() => {
        if (request.isCurrent()) setError('Search failed. Try again.');
      });
  }, [debouncedQuery, search, beginRequest]);

  if (value) {
    return (
      <span className="entity-picker-chip">
        <span>{valueLabel || value}</span>
        <button type="button" aria-label={`Clear ${ariaLabel || 'selection'}`} onClick={() => onChange('', '')}>
          <IoCloseOutline aria-hidden="true" />
        </button>
      </span>
    );
  }

  const pick = (item) => {
    onChange(item.id, item.label);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="entity-picker">
      <input
        type="search"
        value={query}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-controls={listId}
        aria-expanded={open && results.length > 0}
        onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && results.length > 0) {
            event.preventDefault();
            pick(results[0]);
          }
          if (event.key === 'Escape') setOpen(false);
        }}
      />
      {open && (results.length > 0 || error || (debouncedQuery && results.length === 0)) && (
        <ul className="entity-picker-results" id={listId} role="listbox">
          {error && <li className="entity-picker-empty">{error}</li>}
          {!error && results.length === 0 && <li className="entity-picker-empty">No matches</li>}
          {results.map((item) => (
            <li key={item.id} role="option" aria-selected="false">
              <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => pick(item)}>
                <strong>{item.label}</strong>
                {item.sub && <span>{item.sub}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default EntityPicker;
